import React, { useCallback } from "react";
import { Question } from "../../ApiService";
import Button from "../Button/Button";
import { useMarketplace } from "../../contexts/MarketplaceContext";

interface QuestionCardProps {
	question: Question;
	replyValue: string;
	isLoading: boolean;
	onSendReply: (id: string) => void;
	onReplyChange: (id: string, text: string) => void;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
	question,
	replyValue,
	isLoading,
	onSendReply,
	onReplyChange,
}) => {
	const { isWB } = useMarketplace();

	const handleReplyChange = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement>) => {
			onReplyChange(question.id, event.target.value);
		},
		[onReplyChange, question.id]
	);

	const formatDate = (dateString: string) => {
		try {
			// Проверяем, что dateString не пустая и является валидной датой
			if (!dateString) return "Дата не указана";

			const date = new Date(dateString);

			// Проверка на Invalid Date
			if (isNaN(date.getTime())) {
				console.warn("Invalid date:", dateString);
				return "Некорректная дата";
			}

			return date.toLocaleDateString("ru-RU", {
				year: "numeric",
				month: "long",
				day: "numeric",
				hour: "2-digit",
				minute: "2-digit",
			});
		} catch (error) {
			console.error("Error formatting date:", error);
			return "Ошибка форматирования даты";
		}
	};

	// Формирование ссылки на товар в зависимости от маркетплейса
	const getProductUrl = (): string | undefined => {
		// Логируем для отладки
		console.log(`[QuestionCard] Generating product URL for question ${question.id}:`, {
			isWB,
			marketplace: question.marketplace,
			linkId: question.linkId,
			sku: question.sku,
			wb_nmId: question.wb_nmId,
			ozon_sku: question.ozon_sku,
			productId: question.productId
		});

		// Проверяем наличие linkId и что это не undefined
		if (question.linkId !== undefined && question.linkId !== null) {
			if (isWB) {
				return `https://www.wildberries.ru/catalog/${question.linkId}/detail.aspx`;
			} else {
				return `https://www.ozon.ru/product/${question.linkId}`;
			}
		}

		// Для обратной совместимости используем sku
		if (question.sku !== undefined && question.sku !== null) {
			if (isWB) {
				return `https://www.wildberries.ru/catalog/${question.sku}/detail.aspx`;
			} else {
				return `https://www.ozon.ru/product/${question.sku}`;
			}
		}

		// Для WB можно также использовать wb_nmId
		if ((isWB || question.marketplace === 'wb') && question.wb_nmId) {
			return `https://www.wildberries.ru/catalog/${question.wb_nmId}/detail.aspx`;
		}

		// Для Ozon можно использовать ozon_sku
		if ((!isWB || question.marketplace === 'ozon') && question.ozon_sku) {
			return `https://www.ozon.ru/product/${question.ozon_sku}`;
		}

		// Если ничего не найдено, возвращаем undefined
		console.log(`[QuestionCard] No product URL found for question ${question.id}`);
		return undefined;
	};

	const productUrl = getProductUrl();

	return (
		<div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
			<div className="flex justify-between items-start mb-4">
				<div className="flex-1">
					<div className="flex items-center gap-3 mb-2">
						<span className="font-medium text-gray-800">{question.userName}</span>
						<span className="text-sm text-gray-500">{formatDate(question.createdDate)}</span>
					</div>
					<h4 className="font-semibold text-lg text-gray-900 mb-2">
						{productUrl ? (
							<a
								href={productUrl}
								target="_blank"
								rel="noopener noreferrer"
								className="text-blue-600 hover:underline"
							>
								{question.productName}
							</a>
						) : (
							<span>{question.productName}</span>
						)}
					</h4>
					{question.sku && (
						<p className="text-sm text-gray-600 mb-2">
							SKU: {productUrl ? (
								<a
									href={productUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="text-blue-600 hover:underline"
								>
									{question.sku}
								</a>
							) : (
								question.sku
							)}
						</p>
					)}
				</div>
			</div>

			<div className="mb-4">
				<p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
					<strong>Вопрос:</strong> {question.text}
				</p>
			</div>

			<div className="mb-4">
				<label className="block text-sm font-medium text-gray-700 mb-2">
					Ваш ответ на вопрос:
				</label>
				<textarea
					value={replyValue || ""}
					onChange={handleReplyChange}
					placeholder="Введите ответ на вопрос..."
					className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
					rows={4}
					disabled={isLoading}
				/>
				{isLoading && (
					<p className="text-sm text-blue-600 mt-1">Отправляется...</p>
				)}
			</div>

			<div className="flex justify-end items-center w-full mt-2">
				<Button
					title="Отправить"
					status={
						isLoading || !replyValue?.trim()
							? "disabled"
							: "enabled"
					}
					onClick={() => onSendReply(question.id)}
				/>
			</div>

			{question.answer && (
				<div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
					<p className="text-sm text-green-800">
						<strong>Текущий ответ:</strong> {question.answer}
					</p>
				</div>
			)}
		</div>
	);
};

export default QuestionCard; 